import type { AuditActorType, AuditCategory, AuditOutcome } from '@shared/api/contracts';

export interface AuditLogFilters {
  category: AuditCategory[];
  eventType: string[];
  outcome: AuditOutcome[];
  actorType: AuditActorType[];
  actorUserId: string;
  subjectUserId: string;
  projectId: string;
  targetType: string;
  targetId: string;
  requestId: string;
  dateFrom: string;
  dateTo: string;
}

export const EMPTY_AUDIT_FILTERS: AuditLogFilters = {
  category: [],
  eventType: [],
  outcome: [],
  actorType: [],
  actorUserId: '',
  subjectUserId: '',
  projectId: '',
  targetType: '',
  targetId: '',
  requestId: '',
  dateFrom: '',
  dateTo: '',
};

export type AuditFilterError =
  | 'periodOrder'
  | 'periodTooLong'
  | 'actorUserId'
  | 'subjectUserId'
  | 'projectId'
  | 'targetType'
  | 'targetId'
  | 'targetIdRequiresType'
  | 'requestId'
  | 'dateFrom'
  | 'dateTo';

export const AUDIT_PERIOD_MAX_MS = 93 * 24 * 60 * 60 * 1000;

const CATEGORIES = new Set<string>(['auth', 'users', 'api', 'invoices', 'balances', 'aml', 'system']);
const OUTCOMES = new Set<string>(['SUCCESS', 'FAILURE', 'DENIED']);
const ACTORS = new Set<string>([
  'USER',
  'ADMIN',
  'API_KEY',
  'INTERNAL_API',
  'SYSTEM',
  'CLI',
  'DB_TRIGGER',
  'ANONYMOUS',
]);

const splitCsv = (value: string | null): string[] =>
  (value ?? '')
    .split(',')
    .map((part) => part.trim())
    .filter(Boolean);

const known = <T extends string>(values: string[], allowed: Set<string>): T[] =>
  values.filter((value): value is T => allowed.has(value));

export const parseFilters = (params: URLSearchParams): AuditLogFilters => ({
  category: known<AuditCategory>(splitCsv(params.get('category')), CATEGORIES),
  eventType: splitCsv(params.get('eventType')),
  outcome: known<AuditOutcome>(splitCsv(params.get('outcome')), OUTCOMES),
  actorType: known<AuditActorType>(splitCsv(params.get('actorType')), ACTORS),
  actorUserId: params.get('actorUserId')?.trim() ?? '',
  subjectUserId: params.get('subjectUserId')?.trim() ?? '',
  projectId: params.get('projectId')?.trim() ?? '',
  targetType: params.get('targetType')?.trim() ?? '',
  targetId: params.get('targetId')?.trim() ?? '',
  requestId: params.get('requestId')?.trim() ?? '',
  dateFrom: params.get('dateFrom')?.trim() ?? '',
  dateTo: params.get('dateTo')?.trim() ?? '',
});

const isPositiveInt = (value: string): boolean => /^[1-9]\d*$/.test(value);

const setIf = (params: URLSearchParams, key: string, value: string) => {
  if (value) params.set(key, value);
};

export const serializeFilters = (filters: AuditLogFilters): URLSearchParams => {
  const params = new URLSearchParams();
  setIf(params, 'category', filters.category.join(','));
  setIf(params, 'eventType', filters.eventType.join(','));
  setIf(params, 'outcome', filters.outcome.join(','));
  setIf(params, 'actorType', filters.actorType.join(','));
  setIf(params, 'actorUserId', filters.actorUserId.trim());
  setIf(params, 'subjectUserId', filters.subjectUserId.trim());
  setIf(params, 'projectId', filters.projectId.trim());
  setIf(params, 'targetType', filters.targetType.trim());
  if (filters.targetType.trim() && filters.targetId.trim()) {
    params.set('targetId', filters.targetId.trim());
  }
  setIf(params, 'requestId', filters.requestId.trim());
  setIf(params, 'dateFrom', filters.dateFrom.trim());
  setIf(params, 'dateTo', filters.dateTo.trim());
  return params;
};

export const filtersEqual = (left: AuditLogFilters, right: AuditLogFilters): boolean =>
  serializeFilters(left).toString() === serializeFilters(right).toString();

const parseIso = (value: string): number | null => {
  if (!value) return null;
  const time = new Date(value).getTime();
  return Number.isNaN(time) ? null : time;
};

export const validateFilters = (filters: AuditLogFilters): AuditFilterError[] => {
  const errors: AuditFilterError[] = [];
  const actorUserId = filters.actorUserId.trim();
  const subjectUserId = filters.subjectUserId.trim();
  const projectId = filters.projectId.trim();
  const targetType = filters.targetType.trim();
  const targetId = filters.targetId.trim();
  const requestId = filters.requestId.trim();

  if (actorUserId && !isPositiveInt(actorUserId)) errors.push('actorUserId');
  if (subjectUserId && !isPositiveInt(subjectUserId)) errors.push('subjectUserId');
  if (projectId.length > 64) errors.push('projectId');
  if (targetType.length > 32) errors.push('targetType');
  if (targetId.length > 64) errors.push('targetId');
  if (targetId && !targetType) errors.push('targetIdRequiresType');
  if (requestId.length > 64) errors.push('requestId');

  const from = parseIso(filters.dateFrom.trim());
  const to = parseIso(filters.dateTo.trim());
  if (filters.dateFrom.trim() && from === null) errors.push('dateFrom');
  if (filters.dateTo.trim() && to === null) errors.push('dateTo');
  if (from !== null && to !== null) {
    if (from > to) errors.push('periodOrder');
    else if (to - from > AUDIT_PERIOD_MAX_MS) errors.push('periodTooLong');
  }

  return errors;
};

export const toListSearch = (
  filters: AuditLogFilters,
  cursor?: string | null,
  limit = 50
): string => {
  const params = serializeFilters(filters);
  params.set('limit', String(limit));
  if (cursor) params.set('cursor', cursor);
  return params.toString();
};

export const toExportSearch = (filters: AuditLogFilters): string => serializeFilters(filters).toString();

export type AuditPeriodPreset = '24h' | '7d' | '30d' | '93d';

const PRESET_MS: Record<AuditPeriodPreset, number> = {
  '24h': 24 * 60 * 60 * 1000,
  '7d': 7 * 24 * 60 * 60 * 1000,
  '30d': 30 * 24 * 60 * 60 * 1000,
  '93d': AUDIT_PERIOD_MAX_MS,
};

export const presetRange = (preset: AuditPeriodPreset, now = new Date()): Pick<
  AuditLogFilters,
  'dateFrom' | 'dateTo'
> => ({
  dateFrom: new Date(now.getTime() - PRESET_MS[preset]).toISOString(),
  dateTo: now.toISOString(),
});

export const toggleValue = <T extends string>(values: T[], value: T): T[] =>
  values.includes(value) ? values.filter((item) => item !== value) : [...values, value];
